import { ApplicationState } from '@app/store'
import {
  actionCreators as accountActions,
  createPreferencesFromResponse,
  createUserFromResponse,
} from '@app/store/modules/account'
import { makeRequest, RequestMethod } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ILoginState {
  readonly error?: string
  readonly isAttemptingLogin: boolean
  readonly returnUrl?: string
}

const LoginStateRecord = Record({
  error: undefined,
  isAttemptingLogin: false,
  returnUrl: undefined,
})

export class LoginState extends LoginStateRecord implements ILoginState {
  public static fromJS(initialState?: Partial<ILoginState>) {
    return new LoginState(initialState)
  }

  public readonly error?: string
  public readonly isAttemptingLogin: boolean
  public readonly returnUrl: string

  constructor(props?: Partial<ILoginState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum LoginActionTypes {
  AttemptLogin = 'LOGIN::ATTEMPT_LOGIN',
  Init = 'LOGIN::INIT',
  LoginFailure = 'LOGIN::LOGIN_FAILURE',
  LoginRequest = 'LOGIN::LOGIN_REQUEST',
  LoginSuccess = 'LOGIN::LOGIN_SUCCESS',
}

interface IAttemptLoginAction {
  type: LoginActionTypes.AttemptLogin
  username: string
  password: string
  rememberMe: boolean
}

interface ILoginFailureAction {
  type: LoginActionTypes.LoginFailure
  error: string
}

interface ILoginRequestAction {
  type: LoginActionTypes.LoginRequest
}

interface ILoginSuccessAction {
  type: LoginActionTypes.LoginSuccess
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoginFailureAction
  | ILoginRequestAction
  | ILoginSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------

function attemptLogin(
  username: string,
  password: string,
  rememberMe: boolean,
): IAttemptLoginAction {
  return {
    password,
    rememberMe,
    type: LoginActionTypes.AttemptLogin,
    username,
  }
}

function loginFailure(error: string): ILoginFailureAction {
  return {
    error,
    type: LoginActionTypes.LoginFailure,
  }
}

function loginRequest(): ILoginRequestAction {
  return {
    type: LoginActionTypes.LoginRequest,
  }
}

function LoginSuccess(): ILoginSuccessAction {
  return {
    type: LoginActionTypes.LoginSuccess,
  }
}

export const actionCreators = {
  attemptLogin,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<LoginState> = (
  state: LoginState = new LoginState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case LoginActionTypes.LoginFailure:
      return state
        .set('isAttemptingLogin', false)
        .set('error', action.error) as LoginState

    case LoginActionTypes.LoginRequest:
      return state.set('isAttemptingLogin', true) as LoginState

    case LoginActionTypes.LoginSuccess:
      return state
        .set('error', undefined)
        .set('isAttemptingLogin', false) as LoginState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------

function* attemptLoginSaga(action: IAttemptLoginAction) {
  try {
    yield put(loginRequest())

    const loginOpts = {
      body: JSON.stringify({
        password: action.password,
        username: action.username,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeRequest('/api/Users/Login', loginOpts)

    if (result.success === true) {
      const user = createUserFromResponse(result)
      const preferences = createPreferencesFromResponse(result)

      if (user) {
        yield put(
          accountActions.setLoggedInUser(
            result.accessToken,
            user,
            action.rememberMe,
            preferences,
          ),
        )
        yield put(LoginSuccess())
      } else {
        yield put(loginFailure('Unable to login invalid credentials'))
      }
    } else {
      yield put(loginFailure(result.message))
    }
  } catch (err) {
    yield put(loginFailure(err.message))
  }
}

function* watchAttemptLogin() {
  yield takeLatest(LoginActionTypes.AttemptLogin, attemptLoginSaga)
}

export function* loginRootSaga() {
  yield fork(watchAttemptLogin)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = (state: ApplicationState) => state.login

export const selectError = () =>
  createSelector(
    selectModuleState,
    (loginState) => loginState.error,
  )

export const selectIsAttemptingLogin = () =>
  createSelector(
    selectModuleState,
    (loginState) => loginState.isAttemptingLogin,
  )

export const selectReturnUrl = () =>
  createSelector(
    selectModuleState,
    (loginState) => loginState.returnUrl,
  )
